import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  GestureResponderEvent,
  ImageSourcePropType,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableHighlight,
  View,
} from 'react-native';

import { Theme } from '@constants';
import { useContextReference } from '@hooks';

import { styles } from './styles';

import { FocusableHighlight, FocusableHighlightProps } from '../FocusableHighlight/FocusableHighlight';
import { TWCButtonContent } from '../TWCButtonContent/TWCButtonContent';

export const TWCButton = forwardRef<TouchableHighlight, TWCButtonProps>(
  (
    {
      disabled = false,
      accessibilityHint,
      accessibilityLabel,
      testID,
      title,
      icon,
      iconFocused,
      countDown,
      style,
      styleFocused,
      textStyle,
      textFocusStyle,
      onPress,
      onPressCountDown = false,
      onFocus,
      onDidFinishCountDown,
      onBlur,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      width = 396,
      height = 84,
      shouldUseContextReference = true,
      progress,
      countdownSecs = 5,
      onRenderCountDown = false,
      countDownTitle,
    },
    reference,
  ) => {
    const progressReference = useRef(new Animated.Value(0)).current;
    const [progressValue, setProgressValue] = useState(0);
    const [animating, setAnimating] = useState(false);

    const contextReference = useContextReference<TouchableHighlight>(testID);

    const defaultStyle = StyleSheet.flatten([
      {
        width: Theme.px(width),
        minWidth: Theme.px(width),
        height: Theme.px(height),
      },
      styles.button,
      style,
    ]);

    useEffect(() => {
      if (onRenderCountDown) {
        setAnimating(true);
        startAnimation();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRenderCountDown]);

    useEffect(() => {
      const animationListener = progressReference.addListener(({ value }) => {
        setProgressValue(Math.ceil(countdownSecs - value * countdownSecs));
      });

      if (countDown) {
        startProgress();
      }
      return () => {
        progressReference.removeListener(animationListener);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countDown]);

    const startAnimation = () => {
      Animated.timing(progressReference, {
        toValue: 1,
        useNativeDriver: false,
        duration: countdownSecs * 1000,
        easing: Easing.linear,
      }).start(({ finished }) => {
        if (finished) {
          setProgressValue(0);
          setAnimating(false);
          progressReference.stopAnimation();

          if (onDidFinishCountDown) {
            onDidFinishCountDown();
          }
        }
      });
    };

    const startProgress = () => {
      if (!animating) {
        setAnimating(true);
        startAnimation();
      }
    };

    const onButtonPress = (event: GestureResponderEvent) => {
      if (onPress) {
        onPress(event);

        if (onPressCountDown) {
          startProgress();
        }
      }
    };

    return disabled ? (
      <View style={defaultStyle}>
        <TWCButtonContent
          disabled={disabled}
          icon={animating ? undefined : icon}
          iconFocused={iconFocused}
          progress={{
            animated: animating,
            value: animating ? progressReference : progress,
          }}
          styleFocused={styleFocused}
          title={animating ? `${progressValue}s` : title}
        />
      </View>
    ) : (
      <FocusableHighlight
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        accessibilityLiveRegion={'assertive'}
        activeOpacity={1}
        nativeID={testID}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={shouldUseContextReference ? contextReference : reference}
        style={defaultStyle}
        testID={testID}
        underlayColor={Theme.transparent}
        onBlur={onBlur}
        onFocus={onFocus}
        onPress={onButtonPress}
      >
        <TWCButtonContent
          focusStyle={textFocusStyle}
          icon={animating ? undefined : icon}
          iconFocused={iconFocused}
          progress={{
            animated: animating,
            value: animating ? progressReference : progress,
          }}
          styleFocused={styleFocused}
          textStyle={textStyle}
          title={animating ? (countDownTitle ? countDownTitle : `${progressValue}s`) : title}
        />
      </FocusableHighlight>
    );
  },
);

type TWCButtonProps = FocusableHighlightProps & {
  disabled?: boolean;
  title?: string;
  icon?: ImageSourcePropType;
  iconFocused?: ImageSourcePropType;
  countDown?: number;
  textStyle?: StyleProp<TextStyle>;
  textFocusStyle?: StyleProp<TextStyle>;
  onPressCountDown?: boolean;
  onDidFinishCountDown?: () => void;
  width?: number;
  height?: number;
  shouldUseContextReference?: boolean;
  progress?: Animated.Value; // uses only in one file - src/components/onDemand/components/ShowDetailsPageHeader.js
  countdownSecs?: number;
  onRenderCountDown?: boolean;
  countDownTitle?: string;
};
